/**
 * Dropping a card of the hand on the board: which zone takes it, and which
 * cards give back the Joker of a 3-card set.
 */
import { closestCenter, pointerWithin } from "@dnd-kit/core";

// Drop zones inside the table column (the `type` of their <DropZone data>).
const TABLE_ZONES = new Set(["combo", "pending-group", "new-group"]);

const typeOf = (container) => container?.data?.current?.type;
const contains = (r, p) => p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
// How far around the discard pile a single card still reaches it (px).
export const DISCARD_REACH = 90;
const grow = (r, m) => ({ left: r.left - m, right: r.right + m, top: r.top - m, bottom: r.bottom + m });
const overlaps = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/**
 * Collision detection of the board's DndContext. `getTableRect` returns the
 * rectangle of the table column on screen (that column scrolls).
 *
 * - A zone under the finger wins: a group on the table, a group being
 *   composed, the "new group" zone or the discard pile. A drop anywhere on a
 *   group reaches that group, however wide it is and however close its
 *   neighbours sit.
 * - Elsewhere on the table (between two groups, on bare felt) the "new group"
 *   zone takes it, when it is shown: a new group can be dropped anywhere.
 * - Elsewhere (over the hand, with the keyboard) the closest centre wins: it
 *   is what reorders the hand. A finger on a card of the hand always reorders
 *   it, among the hand's cards, even when the hand sits over the table: during
 *   the redeal vote it is lifted to the middle of the table, and the table's
 *   rules (a single card on bare felt goes nowhere) kept its cards from moving.
 * - Table zones scrolled out of sight are left out: they are still measured
 *   where they sit, under the hand for instance.
 * - A single card never makes a new group (3 cards at least): `isSingleCard`
 *   (active id → bool) leaves the "new group" zone out. That card is most
 *   often on its way to the discard pile; dropped on bare felt it goes nowhere.
 *   It also reaches the discard pile from `DISCARD_REACH` px around it, unless
 *   the finger is on a group or on a card of the hand.
 */
export function boardCollision(getTableRect, isSingleCard) {
  return (args) => {
    const table = getTableRect?.();
    const single = !!isSingleCard?.(args.active?.id);
    const shown = args.droppableContainers.filter((c) => {
      if (single && typeOf(c) === "new-group") return false;
      if (!table || !TABLE_ZONES.has(typeOf(c))) return true;
      const rect = args.droppableRects.get(c.id);
      return !!rect && overlaps(rect, table);
    });
    const visible = { ...args, droppableContainers: shown };
    const p = args.pointerCoordinates;
    // On a card of the hand: reorder it, wherever the hand is (hand cards have no type).
    const handCards = args.droppableContainers.filter((c) => !typeOf(c));
    const onHand = !!p && handCards.some((c) => {
      const r = args.droppableRects.get(c.id);
      return !!r && contains(r, p);
    });
    if (onHand) return closestCenter({ ...args, droppableContainers: handCards });
    const under = pointerWithin(visible).filter(({ data }) => {
      const type = typeOf(data.droppableContainer);
      if (type === "discard") return true;
      return TABLE_ZONES.has(type) && (!table || contains(table, args.pointerCoordinates));
    });
    if (under.length > 0) return under;
    // A single card reaches the discard pile from farther away (the finger is
    // not on a card of the hand: that was a reorder, above).
    if (single && p) {
      const discard = args.droppableContainers.find((c) => typeOf(c) === "discard");
      const rect = discard && args.droppableRects.get(discard.id);
      if (rect && contains(grow(rect, DISCARD_REACH), p)) {
        return [{ id: discard.id, data: { droppableContainer: discard, value: 0 } }];
      }
    }
    // Anywhere else on the table (between or below the groups): a new group, when
    // that zone is there. Only cards for a new group are dropped on bare felt;
    // adding to a group still means dropping on it.
    if (table && p && contains(table, p)) {
      if (single) return [];
      const zone = args.droppableContainers.find((c) => typeOf(c) === "new-group");
      if (zone) return [{ id: zone.id, data: { droppableContainer: zone, value: 0 } }];
    }
    return closestCenter(visible);
  };
}

/**
 * Whether the 2 `cards` give back the Joker of a 3-card set: the set's rank,
 * in the 2 suits it lacks.
 */
export function freesSetJoker(combo, cards) {
  if (combo?.kind !== "set" || combo.cards.length !== 3 || cards.length !== 2) return false;
  const real = combo.cards.filter((c) => !c.is_joker);
  if (real.length !== 2) return false;
  const present = new Set(real.map((c) => c.suit));
  const [a, b] = cards;
  return (
    cards.every((c) => c && !c.is_joker && c.rank === real[0].rank && !present.has(c.suit)) &&
    a.suit !== b.suit
  );
}
